import { Injectable, inject } from '@angular/core';

import { Observable, from } from 'rxjs';

import { Page } from '../client-config/page.client-config';
import { ApiOptions } from '../http/http.models';
import { UserConfig } from '../user/user.client-config';
import { DeviceService } from './device/device.service';
import { WINDOW } from './window/window.token';

/**
 * This service uses the native Fetch API to perform HTTP requests with support for keep-alive functionality.
 *
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#keepalive Fetch API documentation}
 *
 * @experimental
 */
@Injectable({
    providedIn: 'root',
})
export class FetchApiService {
    private readonly window = inject(WINDOW);
    private readonly page = inject(Page);
    private readonly deviceService = inject(DeviceService);
    private readonly userConfig = inject(UserConfig);

    /**
     * Makes an API request using the Fetch API.
     * Includes `keepalive` option to guarantee call is not cancelled by the browser when user navigates away or reloads the page.
     *
     * @template T - The expected type of the response data.
     * @param {ApiOptions} options - The options for the HTTP request.
     * @param {string} options.url - The URL to send the request to. This is required.
     * @param {string} [options.method] - The HTTP method to use (e.g., 'GET', 'POST'). Defaults to 'GET'.
     * @param {any} [options.data] - The body data to send with the request. It will be stringified.
     *
     * @returns {Observable<T>} An observable that emits the response data of type `T`.
     * @throws {Error} If the `url` is not provided or if the request fails.
     */
    request<T>(options: ApiOptions): Observable<T> {
        if (!options.url) {
            throw new Error('URL is required for the request.');
        }

        return from(
            this.window
                .fetch(options.url, {
                    method: options.method ?? 'GET',
                    body: JSON.stringify(options.data),
                    headers: {
                        'content-type': 'application/json',
                        'x-bwin-sf-api': this.page.environment,
                        'x-device-type': this.deviceService.deviceType,
                        'x-from-product': this.page.product,
                        'x-xsrf-token': this.userConfig.xsrfToken ?? '',
                        'x-bwin-browser-url': this.window.location.href,
                        ...options.headers,
                    },
                    credentials: 'include',
                    keepalive: true,
                })
                .then(async (response: Response) => {
                    if (response.ok) {
                        if (response.headers.get('content-type')?.includes('application/json')) {
                            return (await response.json()) as T;
                        }

                        return (await response.text()) as T;
                    }

                    throw new Error(`Fetch(${options.url}) failed with status code: ${response.status}.`);
                }),
        );
    }
}
